import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FiSend } from 'react-icons/fi';
import { css } from 'twin.macro';
import { toast } from 'react-toastify';
import { emailDraftService } from '@/_services/api/emailIntegration/emailDraft';
import AutoExpandingTextarea from '@/_elements/Text/AutoExpandingTextarea';
import EmailDraftDeleteButton from './EmailDraftDeleteButton';
import { ActionStyles } from '@/components/Admin/reusableStyles';
import { VscLoading } from 'react-icons/vsc';

const fieldStyle = css`
    display: block;
    width: 100%;
    min-height: 38px;
    border: 1px solid #d8dde6;
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 14px;
`;

const rowStyle = css`
    display: grid;
    grid-template-columns: 84px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
    margin-bottom: 12px;
`;

const labelStyle = css`
    margin-top: 9px;
    font-size: 13px;
    font-weight: 600;
    color: #44505f;
    text-align: right;
    white-space: nowrap;
`;

const recipientsWrapStyle = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const recipientBadgeStyle = css`
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 0.4rem 0.6rem;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    background: #eef2ff;
    color: #334155;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const headerWrap = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
`;

const bodyWrap = css`
    min-height: 80vh;
`;

const EMAIL_REGEX = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

const parseEmails = (value = '') => [
    ...new Set((String(value || '').match(EMAIL_REGEX) || []).map((item) => item.trim().toLowerCase())),
];

const joinList = (items = []) => (items || []).filter(Boolean).join(', ');

const emptyDraft = {
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    body: '',
};

const EmailComposer = ({ show, onClose, chatroomId, draftId, initialDraft, onCreated, onDeleteDraft }) => {
    const [draft, setDraft] = useState(initialDraft || emptyDraft);
    const [recipientToInput, setRecipientToInput] = useState(joinList(initialDraft?.to));
    const [recipientCcInput, setRecipientCcInput] = useState(joinList(initialDraft?.cc));
    const [recipientBccInput, setRecipientBccInput] = useState(joinList(initialDraft?.bcc));
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const composerDraft = draft?.id || draftId ? draft || { ...(initialDraft || {}), id: draftId } : null;

    useEffect(() => {
        if (!show) return;
        const nextDraft = initialDraft || emptyDraft;
        setDraft(nextDraft);
        setRecipientToInput(joinList(nextDraft?.to));
        setRecipientCcInput(joinList(nextDraft?.cc));
        setRecipientBccInput(joinList(nextDraft?.bcc));
    }, [show, initialDraft]);

    useEffect(() => {
        if (!show || !draftId) return;
        let mounted = true;
        const loadDraft = async () => {
            try {
                setLoading(true);
                const res = await emailDraftService.getChatroomEmailDraftById({
                    chatroomId,
                    emaildraftId: draftId,
                });
                const found = res;
                if (mounted && found) {
                    const nextDraft = {
                        id: found.id || found._id,
                        to: Array.isArray(found.to) ? found.to : parseEmails(found.to),
                        cc: Array.isArray(found.cc) ? found.cc : parseEmails(found.cc),
                        bcc: Array.isArray(found.bcc) ? found.bcc : parseEmails(found.bcc),
                        subject: found.subject || '',
                        body: found.body || '',
                    };
                    console.log({ found, nextDraft });
                    setDraft(nextDraft);
                    setRecipientToInput(joinList(nextDraft.to));
                    setRecipientCcInput(joinList(nextDraft.cc));
                    setRecipientBccInput(joinList(nextDraft.bcc));
                }
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return;
                }
                toast.error('Failed to load draft');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        loadDraft();
        return () => {
            mounted = false;
        };
    }, [show, draftId, chatroomId]);

    const handleSave = async () => {
        if (!chatroomId) return null;
        try {
            setSaving(true);
            const draftToSave = draft?.id || draftId;
            const payload = {
                to: parseEmails(recipientToInput),
                cc: parseEmails(recipientCcInput),
                bcc: parseEmails(recipientBccInput),
                subject: draft?.subject || '',
                body: draft?.body || '',
            };
            const res = await emailDraftService.updateChatroomEmailDraftById(draftToSave, {
                chatroomId,
                payload,
            });

            const saved = res;
            setDraft({
                id: saved?.id || saved?._id || draft?.id || draftId,
                to: saved?.to || payload.to,
                cc: saved?.cc || payload.cc,
                bcc: saved?.bcc || payload.bcc,
                subject: saved?.subject || payload.subject,
                body: saved?.body || payload.body,
            });
            setRecipientToInput(joinList(saved?.to || payload.to));
            setRecipientCcInput(joinList(saved?.cc || payload.cc));
            setRecipientBccInput(joinList(saved?.bcc || payload.bcc));
            onCreated?.(
                saved || {
                    id: draft?.id || draftId,
                    ...payload,
                }
            );
            toast.success('Draft saved');
            return saved?._id;
        } catch (error) {
            toast.error(error?.message || 'Failed to save draft');
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleSend = async () => {
        const id = draft?.id || draftId;
        if (!chatroomId || !id) {
            toast.error('No draft to send');
            return;
        }
        try {
            setSending(true);
            const savedDraftId = await handleSave();
            await emailDraftService.sendChatroomEmailDraft(savedDraftId);
            toast.success('Email queued to send');
            onClose?.(true);
        } catch (error) {
            toast.error(error?.message || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal show={show} onHide={() => onClose?.(false)} size="xl" centered>
            <Modal.Header closeButton>
                <div css={headerWrap}>
                    <Modal.Title className="mb-0 d-flex align-items-center gap-2 flex-wrap">
                        <span className="mr-4">Compose/Edit Email</span>
                        <ActionStyles>
                            {composerDraft ? (
                                <EmailDraftDeleteButton
                                    draft={composerDraft}
                                    onDelete={onDeleteDraft}
                                    disabled={saving || sending || loading}
                                    compact
                                    className="ml-1"
                                />
                            ) : null}
                        </ActionStyles>
                    </Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body css={bodyWrap}>
                {loading ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Form>
                        <div css={rowStyle}>
                            <div css={labelStyle}>To</div>
                            <div>
                                <AutoExpandingTextarea
                                    css={fieldStyle}
                                    value={recipientToInput}
                                    onChange={(e) => setRecipientToInput(e.target.value)}
                                    placeholder="Enter emails separated by comma"
                                    minRows={1}
                                />
                                <div css={recipientsWrapStyle}>
                                    {parseEmails(recipientToInput).map((email) => (
                                        <span key={email} css={recipientBadgeStyle} className="badge">
                                            {email}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div css={rowStyle}>
                            <div css={labelStyle}>Cc</div>
                            <div>
                                <AutoExpandingTextarea
                                    css={fieldStyle}
                                    value={recipientCcInput}
                                    onChange={(e) => setRecipientCcInput(e.target.value)}
                                    minRows={1}
                                />
                                <div css={recipientsWrapStyle}>
                                    {parseEmails(recipientCcInput).map((email) => (
                                        <span key={email} css={recipientBadgeStyle} className="badge">
                                            {email}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div css={rowStyle}>
                            <div css={labelStyle}>Bcc</div>
                            <div>
                                <AutoExpandingTextarea
                                    css={fieldStyle}
                                    value={recipientBccInput}
                                    onChange={(e) => setRecipientBccInput(e.target.value)}
                                    minRows={1}
                                    allowResize={true}
                                />
                                <div css={recipientsWrapStyle}>
                                    {parseEmails(recipientBccInput).map((email) => (
                                        <span key={email} css={recipientBadgeStyle} className="badge">
                                            {email}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div css={rowStyle}>
                            <div css={labelStyle}>Subject</div>
                            <Form.Control
                                type="text"
                                css={fieldStyle}
                                value={draft?.subject || ''}
                                onChange={(e) => setDraft((prev) => ({ ...prev, subject: e.target.value }))}
                                placeholder="Subject"
                            />
                        </div>

                        <div css={rowStyle}>
                            <div css={labelStyle}>Body</div>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                css={fieldStyle}
                                value={draft?.body || ''}
                                onChange={(e) => setDraft((prev) => ({ ...prev, body: e.target.value }))}
                                placeholder="Write your email body"
                            />
                        </div>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => onClose?.(false)} disabled={saving || sending}>
                    Cancel
                </Button>
                <Button variant="outline-primary" onClick={handleSave} disabled={saving || sending || loading}>
                    {saving ? 'Saving…' : 'Save Draft'}
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSend}
                    disabled={saving || sending || loading || !draft?.id}
                >
                    {sending ? <VscLoading className=" spin me-2" /> : <FiSend className="me-2" />}
                    Send
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EmailComposer;
